import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Lock, Delete, Check } from 'lucide-react';
import { API } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

/**
 * PIN Setup page (light theme).
 * URL: /pin-setup?token=<short_jwt_from_magic_verify>
 *
 * Flow:
 *  - read ?token= from URL → store in localStorage as access_token (5min jwt)
 *  - user enters 6-digit PIN twice → POST /api/auth/set-pin
 *  - success → /dashboard
 */
export default function PinSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginWithToken } = useAuth();

  const [phase, setPhase] = useState('enter');     // enter | confirm | done
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    const tk = params.get('token');
    if (!tk) {
      setError(t('auth.errors.magic_link_invalid'));
      return;
    }
    (async () => {
      try {
        await loginWithToken(tk);
        setTokenReady(true);
      } catch {
        setError(t('auth.errors.magic_link_invalid'));
      }
    })();
  }, [params, t, loginWithToken]);

  const current = phase === 'enter' ? pin : confirmPin;
  const setCurrent = phase === 'enter' ? setPin : setConfirmPin;

  const press = (digit) => {
    if (current.length >= 6) return;
    const v = current + String(digit);
    setCurrent(v);
    setError('');
    if (v.length === 6) {
      if (phase === 'enter') {
        setTimeout(() => setPhase('confirm'), 250);
      } else {
        setTimeout(() => submit(pin, v), 200);
      }
    }
  };
  const back = () => setCurrent(current.slice(0, -1));

  const submit = async (a, b) => {
    if (a !== b) {
      setError(t('auth.pin.mismatch'));
      setConfirmPin(''); setPhase('enter'); setPin('');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/set-pin`, { pin: a });
      setPhase('done');
      // Honor the original entry point: Orb users → /orb-app, web → /dashboard
      const source = (params.get('source') || 'webapp').toLowerCase();
      const next = source === 'orb' ? '/orb-app' : '/dashboard';
      setTimeout(() => navigate(next), 900);
    } catch (e) {
      const code = e.response?.data?.detail;
      const key = `auth.errors.${code}`;
      const tx = t(key);
      setError(tx !== key ? tx : (code || t('common.error')));
      setPin(''); setConfirmPin(''); setPhase('enter');
    }
    setBusy(false);
  };

  if (!tokenReady && !error) {
    return (
      <div style={styles.bg}>
        <div style={styles.card}>
          <p style={{ color: '#475569' }}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.bg} data-testid="pin-setup-page">
      <div style={styles.card} className="auth-card">
        <div style={styles.iconWrap}>
          <Lock size={28} color="#0ea5e9" />
        </div>
        <h1 style={styles.title}>{t('auth.pin.title')}</h1>
        <p style={styles.subtitle}>
          {phase === 'enter' && t('auth.pin.enterHint')}
          {phase === 'confirm' && t('auth.pin.confirmHint')}
          {phase === 'done' && t('auth.pin.done')}
        </p>

        {/* Pin dots */}
        <div style={styles.dotsRow} data-testid="pin-dots">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{
              ...styles.dot,
              background: i < current.length ? '#0ea5e9' : '#e2e8f0',
              transform: i < current.length ? 'scale(1.1)' : 'scale(1)',
            }} />
          ))}
        </div>

        {error && <div style={styles.errBox} data-testid="pin-error">{error}</div>}

        {phase !== 'done' && (
          <div style={styles.pad}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
              <button key={n} onClick={() => press(n)} disabled={busy}
                data-testid={`pin-key-${n}`} style={styles.key}>{n}</button>
            ))}
            <div style={{ ...styles.key, ...styles.keyEmpty }} />
            <button onClick={() => press(0)} disabled={busy}
              data-testid="pin-key-0" style={styles.key}>0</button>
            <button onClick={back} disabled={busy || current.length === 0}
              data-testid="pin-key-back" style={{ ...styles.key, ...styles.keyBack }}>
              <Delete size={20} />
            </button>
          </div>
        )}

        {phase === 'done' && (
          <div style={{ marginTop: 20, color: '#16a34a', fontSize: 15, display: 'flex', gap: 8, justifyContent: 'center' }}>
            <Check size={20} /> {t('auth.pin.success')}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  bg: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg,#f8fafc 0%,#e0f2fe 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  card: {
    background: '#ffffff', borderRadius: 24, padding: '32px 28px',
    width: '100%', maxWidth: 380,
    boxShadow: '0 20px 60px rgba(14,165,233,0.15), 0 0 0 1px rgba(14,165,233,0.05)',
    textAlign: 'center',
  },
  iconWrap: {
    width: 56, height: 56, borderRadius: 16,
    background: 'linear-gradient(135deg, rgba(14,165,233,0.1), rgba(2,132,199,0.05))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 14px',
  },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' },
  subtitle: { margin: '4px 0 22px', fontSize: 14, color: '#64748b' },
  dotsRow: { display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24 },
  dot: { width: 16, height: 16, borderRadius: '50%', transition: 'all 0.2s ease' },
  errBox: {
    marginBottom: 14, padding: '8px 12px', borderRadius: 10,
    background: '#fef2f2', border: '1px solid #fecaca',
    color: '#dc2626', fontSize: 13,
  },
  pad: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginTop: 8,
  },
  key: {
    height: 64, borderRadius: 16, fontSize: 22, fontWeight: 600,
    background: '#f8fafc', border: '1px solid #e2e8f0',
    color: '#0f172a', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s ease',
  },
  keyEmpty: { background: 'transparent', border: 'none', cursor: 'default' },
  keyBack: { background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' },
};
